import { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  green: '#a7ff83',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

const INCOME_CATS = ['BrickLink Sales', 'BrickOwl Sales'];
const EXPENSE_CATS = ['Inventory', 'Shipping', 'Supplies', 'Fees'];
const ALL_CATS = [...INCOME_CATS, ...EXPENSE_CATS];

const CAT_COLORS = {
  'BrickLink Sales': { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  'BrickOwl Sales':  { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  Inventory:         { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  Shipping:          { bg: '#fdf4ff', text: '#7c3aed', border: '#e9d5ff' },
  Supplies:          { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  Fees:              { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  Transfers:         { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
};

function formatMoney(n) {
  return '$' + Number(n || 0).toFixed(2);
}

function formatDate(d) {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayStr() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// ─── Time range helpers ───
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfYear(d) {
  return new Date(d.getFullYear(), 0, 1);
}
function offsetMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isoDate(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function getRangeBounds(range) {
  const now = new Date();
  if (range === 'thisMonth') {
    return { start: isoDate(startOfMonth(now)), end: isoDate(offsetMonths(startOfMonth(now), 1)) };
  }
  if (range === 'lastMonth') {
    return { start: isoDate(offsetMonths(startOfMonth(now), -1)), end: isoDate(startOfMonth(now)) };
  }
  if (range === 'thisYear') {
    return { start: isoDate(startOfYear(now)), end: isoDate(new Date(now.getFullYear() + 1, 0, 1)) };
  }
  return { start: null, end: null }; // allTime
}

function getPriorRangeBounds(range) {
  const now = new Date();
  if (range === 'thisMonth') {
    return { start: isoDate(offsetMonths(startOfMonth(now), -1)), end: isoDate(startOfMonth(now)) };
  }
  if (range === 'lastMonth') {
    return { start: isoDate(offsetMonths(startOfMonth(now), -2)), end: isoDate(offsetMonths(startOfMonth(now), -1)) };
  }
  if (range === 'thisYear') {
    return { start: isoDate(new Date(now.getFullYear() - 1, 0, 1)), end: isoDate(startOfYear(now)) };
  }
  return { start: null, end: null };
}

function inRange(entry, bounds) {
  if (!bounds.start) return true;
  return entry.date >= bounds.start && entry.date < bounds.end;
}

function Finances() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [range, setRange] = useState('thisMonth');
  const [expandedCat, setExpandedCat] = useState(null); // which category card is open
  const [catSearch, setCatSearch] = useState('');
  const [viewMode, setViewMode] = useState('cards');    // 'cards' | 'list'
  const [listSearch, setListSearch] = useState('');
  const [sortKey, setSortKey] = useState('date');       // date|type|category|vendor|account|amount
  const [sortDir, setSortDir] = useState('desc');       // 'asc' | 'desc'

  // Form fields
  const [entryType, setEntryType] = useState('expense'); // 'income' | 'expense' | 'transfer'
  const [date, setDate] = useState(todayStr());
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Inventory');
  const [fromAccount, setFromAccount] = useState('PayPal');
  const [toAccount, setToAccount] = useState('');

  // Receipt scanner state
  const [scanMode, setScanMode] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanChecked, setScanChecked] = useState({});
  const [scanCategory, setScanCategory] = useState('Inventory');
  const [scanError, setScanError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEntries();
  }, []);

  async function loadEntries() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('date', { ascending: false });
      if (error) throw error;
      setEntries(data || []);
    } catch (err) {
      console.error('Failed to load entries:', err);
    }
    setLoading(false);
  }

  async function handleSave() {
    if (!amount || isNaN(parseFloat(amount))) return;

    const record = {
      date: date || todayStr(),
      amount: parseFloat(amount),
      type: entryType,
    };

    if (entryType === 'transfer') {
      record.vendor = null;
      record.category = 'Transfers';
      record.description = description.trim() || null;
      record.from_account = fromAccount.trim() || 'PayPal';
      record.to_account = toAccount.trim() || null;
    } else {
      record.vendor = vendor.trim() || null;
      record.description = description.trim() || null;
      record.category = category;
      record.from_account = null;
      record.to_account = null;
    }

    try {
      if (editId) {
        const { error } = await supabase.from('expenses').update(record).eq('id', editId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('expenses').insert(record);
        if (error) throw error;
      }
      resetForm();
      loadEntries();
    } catch (err) {
      console.error('Save error:', err);
      alert('Save failed — see console.');
    }
  }

  async function handleDelete(id) {
    try {
      const { error } = await supabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
      setConfirmDelete(null);
      loadEntries();
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  function startEdit(exp) {
    setEditId(exp.id);
    setEntryType(exp.type || 'expense');
    setDate(exp.date);
    setVendor(exp.vendor || '');
    setDescription(exp.description || '');
    setAmount(String(exp.amount));
    setCategory(exp.category || 'Inventory');
    setFromAccount(exp.from_account || 'PayPal');
    setToAccount(exp.to_account || '');
    setShowForm(true);
    setScanMode(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setShowForm(false);
    setEditId(null);
    setEntryType('expense');
    setDate(todayStr());
    setVendor('');
    setDescription('');
    setAmount('');
    setCategory('Inventory');
    setFromAccount('PayPal');
    setToAccount('');
    setScanMode(false);
    setScanResult(null);
    setScanPreview(null);
    setScanChecked({});
    setScanError(null);
  }

  // ─── Receipt Scanner ───
  async function handleReceiptFile(file) {
    if (!file) return;
    setScanError(null);
    setScanning(true);
    setScanResult(null);

    const previewUrl = URL.createObjectURL(file);
    setScanPreview(previewUrl);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${PROXY}/api/scan-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64, mimeType: file.type || 'image/jpeg' }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Server error');
      }

      const data = await res.json();
      setScanResult(data);
      const checks = {};
      (data.items || []).forEach((_, i) => { checks[i] = true; });
      setScanChecked(checks);
    } catch (err) {
      console.error('Scan error:', err);
      setScanError(err.message || 'Failed to scan receipt');
    } finally {
      setScanning(false);
    }
  }

  function toggleScanItem(idx) {
    setScanChecked(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  function getScanTotal() {
    if (!scanResult?.items) return 0;
    return scanResult.items
      .filter((_, i) => scanChecked[i])
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  async function saveScanExpense() {
    if (!scanResult) return;
    const checkedItems = (scanResult.items || []).filter((_, i) => scanChecked[i]);
    const total = checkedItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const desc = checkedItems.map(i => i.description).join(', ');

    const record = {
      date: scanResult.date || todayStr(),
      vendor: scanResult.vendor || null,
      description: desc.length > 200 ? desc.substring(0, 197) + '...' : desc,
      amount: Math.round(total * 100) / 100,
      category: scanCategory,
      type: 'expense',
      line_items: JSON.stringify({ items: checkedItems, tax: scanResult.tax, originalTotal: scanResult.total }),
    };

    try {
      const { error } = await supabase.from('expenses').insert(record);
      if (error) throw error;
      resetForm();
      loadEntries();
    } catch (err) {
      console.error('Save scan error:', err);
    }
  }

  // ─── Export CSV (respects current range) ───
  function exportCSV() {
    const bounds = getRangeBounds(range);
    const data = entries.filter(e => inRange(e, bounds));
    const rows = [['Date', 'Type', 'Vendor', 'Description', 'Amount', 'Category', 'From', 'To']];
    data.forEach(e => {
      rows.push([e.date, e.type || 'expense', e.vendor || '', e.description || '', e.amount, e.category, e.from_account || '', e.to_account || '']);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jaykay-finances-${todayStr()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Calculations ───
  const bounds = getRangeBounds(range);
  const priorBounds = getPriorRangeBounds(range);

  const inRangeEntries = entries.filter(e => inRange(e, bounds));
  const priorRangeEntries = entries.filter(e => inRange(e, priorBounds));

  const incomeEntries  = inRangeEntries.filter(e => (e.type || 'expense') === 'income');
  const expenseEntries = inRangeEntries.filter(e => (e.type || 'expense') === 'expense');
  const transferEntries = inRangeEntries.filter(e => e.type === 'transfer');

  const totalIncome   = incomeEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalExpenses = expenseEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalTransfers = transferEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const profit = totalIncome - totalExpenses;

  // Per-category aggregates (current + prior) for the cards
  function aggForCat(catEntries, allEntries) {
    const items = allEntries.filter(e => e.category === catEntries);
    return {
      total: items.reduce((s, e) => s + Number(e.amount || 0), 0),
      count: items.length,
    };
  }

  function getCatAgg(catName) {
    const current = aggForCat(catName, inRangeEntries);
    const prior   = aggForCat(catName, priorRangeEntries);
    return {
      ...current,
      priorTotal: prior.total,
      priorCount: prior.count,
      delta: current.total - prior.total,
      deltaCount: current.count - prior.count,
    };
  }

  // Recent activity — last 5 across all time
  const recent = entries.slice(0, 5);

  // ─── Sortable list view ───
  function sortValue(e, key) {
    switch (key) {
      case 'amount':   return Number(e.amount || 0);
      case 'type':     return e.type || 'expense';
      case 'category': return (e.category || '').toLowerCase();
      case 'vendor':   return (e.type === 'transfer'
                          ? `${e.from_account || ''} ${e.to_account || ''}`
                          : (e.vendor || '')).toLowerCase();
      case 'account':  return (e.from_account || e.to_account || '').toLowerCase();
      case 'date':
      default:         return e.date || '';
    }
  }
  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'date' || key === 'amount' ? 'desc' : 'asc'); // sensible default per column
    }
  }
  const listEntries = inRangeEntries
    .filter(e => {
      if (!listSearch) return true;
      const q = listSearch.toLowerCase();
      return (e.vendor || '').toLowerCase().includes(q)
          || (e.description || '').toLowerCase().includes(q)
          || (e.category || '').toLowerCase().includes(q)
          || (e.from_account || '').toLowerCase().includes(q)
          || (e.to_account || '').toLowerCase().includes(q)
          || (e.type || '').toLowerCase().includes(q)
          || String(e.amount).includes(q);
    })
    .sort((a, b) => {
      const av = sortValue(a, sortKey), bv = sortValue(b, sortKey);
      const sign = sortDir === 'asc' ? 1 : -1;
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      // tie-break by date desc so equal rows stay stable/meaningful
      return (b.date || '').localeCompare(a.date || '');
    });

  const RANGE_OPTIONS = [
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'thisYear', label: 'This Year' },
    { key: 'allTime', label: 'All Time' },
  ];

  // For the add-form's category list
  const activeCats = entryType === 'income' ? INCOME_CATS : EXPENSE_CATS;

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
      <PageHeader title="Finances" icon="$" />

      {/* ─── Summary Cards ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderBottom: '3px solid #16a34a', borderRadius: '6px',
          padding: '14px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#16a34a' }}>{formatMoney(totalIncome)}</div>
          <div style={{ fontSize: '0.6rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>Income</div>
        </div>
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderBottom: '3px solid #dc2626', borderRadius: '6px',
          padding: '14px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#dc2626' }}>{formatMoney(totalExpenses)}</div>
          <div style={{ fontSize: '0.6rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>Expenses</div>
        </div>
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderBottom: `3px solid ${profit >= 0 ? colors.greenDark : '#dc2626'}`,
          borderRadius: '6px', padding: '14px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: profit >= 0 ? colors.greenDark : '#dc2626' }}>
            {profit < 0 ? '-' : ''}{formatMoney(Math.abs(profit))}
          </div>
          <div style={{ fontSize: '0.6rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>Profit</div>
        </div>
      </div>

      {/* Transfer flow strip (only if any transfers in range) */}
      {totalTransfers > 0 && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', padding: '8px 14px', marginBottom: '12px',
          display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem',
        }}>
          <span style={{ color: colors.textSecondary }}>Transferred (not in profit)</span>
          <span style={{ color: '#475569', fontWeight: 700 }}>{formatMoney(totalTransfers)} · {transferEntries.length} {transferEntries.length === 1 ? 'transfer' : 'transfers'}</span>
        </div>
      )}

      {/* ─── Time range selector ─── */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        {RANGE_OPTIONS.map(opt => {
          const isActive = range === opt.key;
          return (
            <button key={opt.key} onClick={() => { setRange(opt.key); setExpandedCat(null); }}
              style={{
                flex: 1, padding: '8px 4px',
                background: isActive ? colors.navy : colors.cardBg,
                color: isActive ? '#fff' : colors.textPrimary,
                border: `1px solid ${isActive ? colors.navy : colors.border}`,
                borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
              }}
            >{opt.label}</button>
          );
        })}
      </div>

      {/* ─── Receipt Scanner ─── */}
      {scanMode && !showForm && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${colors.navy}`, borderRadius: '6px',
          padding: '20px', marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.textPrimary }}>Scan Receipt</div>
            <button onClick={resetForm} style={{ background: 'none', border: 'none', color: colors.textSecondary, fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', cursor: 'pointer' }}>Cancel</button>
          </div>

          {!scanResult && !scanning && (
            <div onClick={() => fileInputRef.current?.click()} style={{
              border: `2px dashed ${colors.border}`, borderRadius: '6px', padding: '40px 20px',
              textAlign: 'center', cursor: 'pointer', marginBottom: '12px', background: '#f9fafb',
            }}>
              <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => handleReceiptFile(e.target.files[0])} />
              <div style={{ fontSize: '2rem', marginBottom: '10px', color: colors.navy }}>&#x2295;</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '4px' }}>Tap to take photo or upload</div>
              <div style={{ fontSize: '0.78rem', color: colors.textSecondary }}>Camera, photo library, or drag &amp; drop</div>
            </div>
          )}

          {scanning && (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              {scanPreview && <img src={scanPreview} alt="Receipt" style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', borderRadius: '6px', marginBottom: '16px', border: `1px solid ${colors.border}` }} />}
              <div style={{ fontSize: '1rem', fontWeight: 700, color: colors.navy, marginBottom: '6px' }}>Reading receipt...</div>
              <div style={{ fontSize: '0.78rem', color: colors.textSecondary }}>This may take a few seconds</div>
            </div>
          )}

          {scanError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '14px', marginBottom: '12px', fontSize: '0.82rem', color: '#dc2626' }}>
              {scanError}
              <button onClick={() => { setScanError(null); setScanPreview(null); setScanResult(null); }} style={{ display: 'block', marginTop: '8px', background: 'none', border: 'none', color: '#dc2626', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Try again</button>
            </div>
          )}

          {scanResult && (
            <div>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '16px', background: '#f9fafb', borderRadius: '6px', padding: '12px' }}>
                {scanPreview && <img src={scanPreview} alt="Receipt" style={{ width: '60px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: `1px solid ${colors.border}`, flexShrink: 0 }} />}
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>{scanResult.vendor || 'Unknown Store'}</div>
                  <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: '2px' }}>{scanResult.date ? formatDate(scanResult.date) : 'Date not detected'}</div>
                  <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: '2px' }}>Receipt total: {formatMoney(scanResult.total)} (tax: {formatMoney(scanResult.tax)})</div>
                </div>
              </div>

              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '0.78rem', color: '#1e40af', fontWeight: 600 }}>
                Uncheck any items that are NOT business expenses
              </div>

              <div style={{ marginBottom: '16px' }}>
                {(scanResult.items || []).map((item, i) => {
                  const checked = scanChecked[i];
                  return (
                    <div key={i} onClick={() => toggleScanItem(i)} style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                      background: checked ? colors.cardBg : '#f9fafb', border: `1px solid ${checked ? colors.border : '#e5e7eb'}`,
                      borderRadius: '6px', marginBottom: '6px', cursor: 'pointer', opacity: checked ? 1 : 0.5, transition: 'all 0.15s ease',
                    }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '4px', border: `2px solid ${checked ? colors.greenDark : '#d1d5db'}`, background: checked ? colors.greenDark : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {checked && <span style={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800 }}>&#x2713;</span>}
                      </div>
                      <div style={{ flex: 1, fontSize: '0.85rem', color: colors.textPrimary, textDecoration: checked ? 'none' : 'line-through' }}>{item.description}</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: checked ? colors.textPrimary : colors.textSecondary, flexShrink: 0 }}>{formatMoney(item.amount)}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '14px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.greenDark }}>Business Total ({Object.values(scanChecked).filter(Boolean).length} items)</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.greenDark }}>{formatMoney(getScanTotal())}</div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Category</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {EXPENSE_CATS.map(cat => {
                    const cc = CAT_COLORS[cat];
                    const sel = scanCategory === cat;
                    return (
                      <button key={cat} onClick={() => setScanCategory(cat)} style={{
                        padding: '8px 14px', background: sel ? cc.text : colors.cardBg, color: sel ? '#fff' : cc.text,
                        border: `1px solid ${sel ? cc.text : cc.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                      }}>{cat}</button>
                    );
                  })}
                </div>
              </div>

              <button onClick={saveScanExpense} disabled={getScanTotal() <= 0} style={{
                width: '100%', padding: '14px', background: getScanTotal() > 0 ? colors.navy : '#e2e4e8',
                color: getScanTotal() > 0 ? '#fff' : colors.textSecondary, border: 'none', borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.9rem', cursor: getScanTotal() > 0 ? 'pointer' : 'not-allowed',
              }}>Save {formatMoney(getScanTotal())} to {scanCategory}</button>
            </div>
          )}
        </div>
      )}

      {/* ─── Add / Edit Form ─── */}
      {showForm && !scanMode && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${editId ? '#d97706' : entryType === 'income' ? '#16a34a' : entryType === 'transfer' ? '#475569' : colors.greenDark}`,
          borderRadius: '6px', padding: '20px', marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.textPrimary }}>
              {editId ? 'Edit Entry' : 'Add Entry'}
            </div>
          </div>

          {/* Type toggle: Income / Expense / Transfer */}
          {!editId && (
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              <button onClick={() => { setEntryType('income'); setCategory('BrickLink Sales'); }}
                style={{
                  flex: 1, padding: '10px',
                  background: entryType === 'income' ? '#16a34a' : colors.cardBg,
                  color: entryType === 'income' ? '#fff' : colors.textPrimary,
                  border: `1px solid ${entryType === 'income' ? '#16a34a' : colors.border}`,
                  borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >Income</button>
              <button onClick={() => { setEntryType('expense'); setCategory('Inventory'); }}
                style={{
                  flex: 1, padding: '10px',
                  background: entryType === 'expense' ? '#dc2626' : colors.cardBg,
                  color: entryType === 'expense' ? '#fff' : colors.textPrimary,
                  border: `1px solid ${entryType === 'expense' ? '#dc2626' : colors.border}`,
                  borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >Expense</button>
              <button onClick={() => setEntryType('transfer')}
                style={{
                  flex: 1, padding: '10px',
                  background: entryType === 'transfer' ? '#475569' : colors.cardBg,
                  color: entryType === 'transfer' ? '#fff' : colors.textPrimary,
                  border: `1px solid ${entryType === 'transfer' ? '#475569' : colors.border}`,
                  borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >Transfer</button>
            </div>
          )}

          {/* Amount + Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Amount</div>
              <input type="number" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} style={{
                width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
              }} />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Date</div>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{
                width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
              }} />
            </div>
          </div>

          {entryType === 'transfer' ? (
            <>
              {/* From / To */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>From</div>
                  <input type="text" value={fromAccount} onChange={e => setFromAccount(e.target.value)} placeholder="PayPal" style={{
                    width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>To</div>
                  <input type="text" value={toAccount} onChange={e => setToAccount(e.target.value)} placeholder="Personal Checking" style={{
                    width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                  }} />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Description (optional)</div>
                <input type="text" placeholder="Note about this transfer..." value={description} onChange={e => setDescription(e.target.value)} style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                }} />
              </div>
            </>
          ) : (
            <>
              {/* Vendor / Source */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  {entryType === 'income' ? 'Source' : 'Vendor'}
                </div>
                <input type="text" placeholder={entryType === 'income' ? 'Order #, buyer name...' : 'Target, BrickLink, USPS...'} value={vendor} onChange={e => setVendor(e.target.value)} style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Description (optional)</div>
                <input type="text" placeholder="What was this for?" value={description} onChange={e => setDescription(e.target.value)} style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                }} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Category</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {activeCats.map(cat => {
                    const cc = CAT_COLORS[cat];
                    const sel = category === cat;
                    return (
                      <button key={cat} onClick={() => setCategory(cat)} style={{
                        padding: '8px 14px', background: sel ? cc.text : colors.cardBg, color: sel ? '#fff' : cc.text,
                        border: `1px solid ${sel ? cc.text : cc.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                        fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                      }}>{cat}</button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSave} disabled={!amount || isNaN(parseFloat(amount))} style={{
              flex: 2, padding: '14px',
              background: amount && !isNaN(parseFloat(amount)) ? (entryType === 'income' ? '#16a34a' : entryType === 'transfer' ? '#475569' : colors.navy) : '#e2e4e8',
              color: amount && !isNaN(parseFloat(amount)) ? '#fff' : colors.textSecondary,
              border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.9rem',
              cursor: amount && !isNaN(parseFloat(amount)) ? 'pointer' : 'not-allowed',
            }}>
              {editId ? 'Update' : entryType === 'income' ? 'Save Income' : entryType === 'transfer' ? 'Save Transfer' : 'Save Expense'}
            </button>
            <button onClick={resetForm} style={{
              flex: 1, padding: '14px', background: colors.cardBg, color: colors.textSecondary,
              border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
              fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add buttons (only when no form/scan is open) */}
      {!showForm && !scanMode && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button onClick={() => setScanMode(true)} style={{
            flex: 1, padding: '14px', background: colors.navy, color: '#fff', border: 'none', borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
          }}>&#x2295; Scan Receipt</button>
          <button onClick={() => setShowForm(true)} style={{
            flex: 1, padding: '14px', background: colors.cardBg, color: colors.navy,
            border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
            fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
          }}>+ Add Entry</button>
        </div>
      )}

      {/* ─── Category Cards ─── */}
      {!loading && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[['cards', 'Categories'], ['list', 'List']].map(([mode, label]) => (
                <button key={mode} onClick={() => setViewMode(mode)} style={{
                  background: viewMode === mode ? colors.navy : colors.cardBg,
                  color: viewMode === mode ? '#fff' : colors.textSecondary,
                  border: `1px solid ${viewMode === mode ? colors.navy : colors.border}`,
                  borderRadius: '6px', padding: '5px 12px', fontFamily: "'Outfit', sans-serif",
                  fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                }}>{label}</button>
              ))}
            </div>
            <button onClick={exportCSV} style={{
              background: 'none', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '4px 10px',
              fontFamily: "'Outfit', sans-serif", fontSize: '0.68rem', fontWeight: 700, color: colors.navy, cursor: 'pointer',
            }}>Export CSV</button>
          </div>

          {/* Income then Expenses then Transfers */}
          {viewMode === 'cards' && [...INCOME_CATS, ...EXPENSE_CATS].map(cat => {
            const agg = getCatAgg(cat);
            const cc = CAT_COLORS[cat];
            const isIncome = INCOME_CATS.includes(cat);
            const isOpen = expandedCat === cat;
            const noun = isIncome ? (agg.count === 1 ? 'order' : 'orders') : (agg.count === 1 ? 'receipt' : 'receipts');
            // Match entries for this category, then filter by search
            const catEntries = inRangeEntries
              .filter(e => e.category === cat)
              .filter(e => {
                if (!catSearch || !isOpen) return true;
                const q = catSearch.toLowerCase();
                return (e.vendor || '').toLowerCase().includes(q)
                    || (e.description || '').toLowerCase().includes(q)
                    || String(e.amount).includes(q);
              });
            return (
              <CategoryCard key={cat} cat={cat} cc={cc} agg={agg} noun={noun}
                isOpen={isOpen} entries={catEntries}
                onToggle={() => { setExpandedCat(isOpen ? null : cat); setCatSearch(''); }}
                searchValue={catSearch} onSearch={setCatSearch}
                onEdit={startEdit} confirmDelete={confirmDelete}
                setConfirmDelete={setConfirmDelete} handleDelete={handleDelete}
              />
            );
          })}

          {/* Transfers card (always show; useful even at $0 as a hint) */}
          {viewMode === 'cards' && (() => {
            const cat = 'Transfers';
            const cc = CAT_COLORS[cat];
            const current = inRangeEntries.filter(e => e.type === 'transfer');
            const prior = priorRangeEntries.filter(e => e.type === 'transfer');
            const agg = {
              total: current.reduce((s, e) => s + Number(e.amount || 0), 0),
              count: current.length,
              priorTotal: prior.reduce((s, e) => s + Number(e.amount || 0), 0),
              priorCount: prior.length,
            };
            agg.delta = agg.total - agg.priorTotal;
            agg.deltaCount = agg.count - agg.priorCount;
            const isOpen = expandedCat === cat;
            const noun = agg.count === 1 ? 'transfer' : 'transfers';
            const tEntries = current.filter(e => {
              if (!catSearch || !isOpen) return true;
              const q = catSearch.toLowerCase();
              return (e.from_account || '').toLowerCase().includes(q)
                  || (e.to_account || '').toLowerCase().includes(q)
                  || (e.description || '').toLowerCase().includes(q)
                  || String(e.amount).includes(q);
            });
            return (
              <CategoryCard cat={cat} cc={cc} agg={agg} noun={noun}
                isOpen={isOpen} entries={tEntries} isTransfer
                onToggle={() => { setExpandedCat(isOpen ? null : cat); setCatSearch(''); }}
                searchValue={catSearch} onSearch={setCatSearch}
                onEdit={startEdit} confirmDelete={confirmDelete}
                setConfirmDelete={setConfirmDelete} handleDelete={handleDelete}
              />
            );
          })()}

          {/* ─── Sortable list view ─── */}
          {viewMode === 'list' && (
            <>
              <input
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search buyer/seller, description, category, account…"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: '10px',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem',
                }}
              />
              {listEntries.length === 0 ? (
                <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '24px', textAlign: 'center', color: colors.textSecondary, fontSize: '0.85rem' }}>
                  No transactions in this range{listSearch ? ' match your search' : ''}.
                </div>
              ) : (
                <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {[
                            ['date', 'Date', 'left'],
                            ['type', 'Type', 'left'],
                            ['category', 'Category', 'left'],
                            ['vendor', 'Buyer / Seller', 'left'],
                            ['account', 'Account', 'left'],
                            ['amount', 'Amount', 'right'],
                          ].map(([key, label, align]) => (
                            <th key={key} onClick={() => toggleSort(key)} style={{
                              textAlign: align, padding: '10px 12px', cursor: 'pointer', userSelect: 'none',
                              fontSize: '0.64rem', fontWeight: 800, color: colors.navy,
                              textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                              borderBottom: `2px solid ${colors.border}`,
                            }}>
                              {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                          <th style={{ borderBottom: `2px solid ${colors.border}` }} />
                        </tr>
                      </thead>
                      <tbody>
                        {listEntries.map(exp => {
                          const isIncome = (exp.type || 'expense') === 'income';
                          const isTransfer = exp.type === 'transfer';
                          const party = isTransfer
                            ? `${exp.from_account || '?'} → ${exp.to_account || '?'}`
                            : (exp.vendor || exp.description || '—');
                          const account = isTransfer ? '' : (exp.from_account || exp.to_account || '');
                          return (
                            <tr key={exp.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: colors.textSecondary }}>{formatDate(exp.date)}</td>
                              <td style={{ padding: '9px 12px' }}>
                                <span style={{
                                  fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', padding: '2px 7px', borderRadius: '4px',
                                  background: isIncome ? '#dcfce7' : isTransfer ? '#e2e8f0' : '#fee2e2',
                                  color: isIncome ? '#16a34a' : isTransfer ? '#475569' : '#dc2626',
                                }}>{isIncome ? 'Income' : isTransfer ? 'Transfer' : 'Expense'}</span>
                              </td>
                              <td style={{ padding: '9px 12px', color: colors.textPrimary }}>{exp.category}</td>
                              <td style={{ padding: '9px 12px', color: colors.textPrimary, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{party}</td>
                              <td style={{ padding: '9px 12px', color: colors.textSecondary }}>{account}</td>
                              <td style={{ padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 800,
                                color: isIncome ? '#16a34a' : isTransfer ? '#475569' : colors.textPrimary }}>
                                {isIncome ? '+' : ''}{formatMoney(exp.amount)}
                              </td>
                              <td style={{ padding: '9px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                <button onClick={() => startEdit(exp)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: '0.9rem', padding: '2px 4px' }}>✎</button>
                                {confirmDelete === exp.id ? (
                                  <button onClick={() => handleDelete(exp.id)} title="Confirm delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', fontWeight: 800, padding: '2px 4px' }}>Sure?</button>
                                ) : (
                                  <button onClick={() => setConfirmDelete(exp.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: '0.9rem', padding: '2px 4px' }}>🗑</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: '8px 12px', fontSize: '0.68rem', color: colors.textSecondary, borderTop: `1px solid ${colors.border}` }}>
                    {listEntries.length} transaction{listEntries.length === 1 ? '' : 's'} · tap any column heading to sort
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ─── Recent activity ─── */}
      {!loading && recent.length > 0 && (
        <>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '24px', marginBottom: '10px' }}>
            Recent Activity
          </div>
          {recent.map(exp => {
            const cc = CAT_COLORS[exp.category] || CAT_COLORS.Inventory;
            const isIncome = (exp.type || 'expense') === 'income';
            const isTransfer = exp.type === 'transfer';
            return (
              <div key={exp.id} style={{
                background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px',
                padding: '10px 14px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: isIncome ? '#16a34a' : isTransfer ? '#475569' : cc.text, flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {isTransfer
                      ? `${exp.from_account || '?'} → ${exp.to_account || '?'}`
                      : (exp.vendor || exp.description || exp.category)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                    {formatDate(exp.date)} · {exp.category}
                  </div>
                </div>
                <div style={{
                  fontSize: '0.9rem', fontWeight: 800, whiteSpace: 'nowrap',
                  color: isIncome ? '#16a34a' : isTransfer ? '#475569' : colors.textPrimary,
                }}>
                  {isIncome ? '+' : ''}{formatMoney(exp.amount)}
                </div>
              </div>
            );
          })}
        </>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>Loading...</div>
      )}

      {!loading && entries.length === 0 && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px',
          padding: '40px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>$</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>No entries yet</div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>Scan a receipt or add an entry to start tracking.</div>
        </div>
      )}
    </div>
  );
}

// ─── CategoryCard component ───
function CategoryCard({ cat, cc, agg, noun, isOpen, entries, isTransfer, onToggle, searchValue, onSearch, onEdit, confirmDelete, setConfirmDelete, handleDelete }) {
  const deltaPositive = agg.delta > 0;
  const deltaText = agg.delta === 0
    ? 'no change'
    : `${deltaPositive ? '+' : '-'}${formatMoney(Math.abs(agg.delta))} vs prior`;
  // For income, positive delta = good (green). For expenses, positive delta = bad (red). For transfers, neutral.
  const isIncome = ['BrickLink Sales', 'BrickOwl Sales'].includes(cat);
  let deltaColor = colors.textSecondary;
  if (agg.delta !== 0 && !isTransfer) {
    if (isIncome) deltaColor = deltaPositive ? '#16a34a' : '#dc2626';
    else          deltaColor = deltaPositive ? '#dc2626' : '#16a34a';
  }

  return (
    <div style={{
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${cc.text}`,
      borderRadius: '6px', marginBottom: '8px', overflow: 'hidden',
    }}>
      <div onClick={onToggle} style={{
        padding: '14px 16px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 800, color: colors.textPrimary }}>{cat}</div>
          <div style={{ fontSize: '0.7rem', color: colors.textSecondary, marginTop: '2px' }}>
            {agg.count} {noun}
            {agg.count > 0 && <span style={{ color: deltaColor, marginLeft: '8px', fontWeight: 600 }}>· {deltaText}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: cc.text }}>{formatMoney(agg.total)}</div>
        </div>
        <div style={{ color: colors.textSecondary, fontSize: '0.8rem', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>▼</div>
      </div>

      {isOpen && (
        <div style={{ borderTop: `1px solid ${colors.border}`, padding: '12px 16px', background: '#f9fafb' }}>
          <input
            type="text" value={searchValue} onChange={e => onSearch(e.target.value)}
            placeholder={isTransfer ? "Search from/to/description..." : "Search vendor or description..."}
            style={{
              width: '100%', padding: '8px 12px', background: '#fff',
              border: `1px solid ${colors.border}`, borderRadius: '6px',
              fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem',
              outline: 'none', marginBottom: '10px', boxSizing: 'border-box',
            }}
          />

          {entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary, fontSize: '0.82rem' }}>
              {searchValue ? 'No matches' : 'No entries in this range'}
            </div>
          ) : entries.map(exp => (
            <div key={exp.id} style={{
              background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '6px',
              padding: '10px 12px', marginBottom: '6px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                <div style={{
                  fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isTransfer
                    ? `${exp.from_account || '?'} → ${exp.to_account || '?'}`
                    : (exp.vendor || exp.description || exp.category)}
                </div>
                <div style={{
                  fontSize: '0.9rem', fontWeight: 800, flexShrink: 0,
                  color: isTransfer ? '#475569' : (exp.type === 'income' ? '#16a34a' : colors.textPrimary),
                }}>
                  {exp.type === 'income' ? '+' : ''}{formatMoney(exp.amount)}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                  {formatDate(exp.date)}
                  {exp.description && exp.vendor && <span> · {exp.description}</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => onEdit(exp)} style={{
                    background: 'none', border: 'none', fontSize: '0.7rem', color: colors.navy,
                    cursor: 'pointer', fontFamily: "'Outfit', sans-serif", fontWeight: 600, padding: '2px 4px',
                  }}>edit</button>
                  {confirmDelete === exp.id ? (
                    <button onClick={() => handleDelete(exp.id)} style={{
                      background: 'none', border: 'none', fontSize: '0.7rem', color: '#dc2626',
                      cursor: 'pointer', fontFamily: "'Outfit', sans-serif", fontWeight: 700, padding: '2px 4px',
                    }}>confirm?</button>
                  ) : (
                    <button onClick={() => setConfirmDelete(exp.id)} style={{
                      background: 'none', border: 'none', fontSize: '0.7rem', color: '#dc2626',
                      cursor: 'pointer', fontFamily: "'Outfit', sans-serif", fontWeight: 600, padding: '2px 4px',
                    }}>delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Finances;